import {
	CardBody,
	CardFooter,
	CardHeading,
	DataListSearch,
	IconButton,
	PaginatedListFetcherConfig,
	PaginatedListProvider,
	Pagination,
	SwitchControl,
	laravelPaginatedListFetchConfig,
	useEditDrawer,
	usePaginatedList,
} from "@dgs/core";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useFilterQuery } from "../../../../shared/api/filterQueries";
import { CreateEventDrawer } from "../../../../shared/events/save/CreateEventDrawer";
import { useFilter } from "../../../../shared/filters";
import { EventFilterDrawer } from "../../../../shared/filters/events/EventFilterDrawer";
import { FilterBar } from "../../../../shared/filters/FilterBar";
import { EventListOverviewResource } from "../../../../shared/types/event";
import { eventService } from "../../shared/api/eventService";
import { EventListCalendarSwitcher } from "../EventListCalendarSwitcher";
import { EventScopeProps } from "../EventsPage";
import { useEventSave } from "../useEventSave";
import { EventOverviewCard } from "./EventOverviewCard";

const Wrapper = styled.div`
	display: grid;
`;

const Header = styled.div`
	display: grid;
`;

const HeaderTop = styled.div`
	display: grid;
	grid-template-rows: repeat(2, min-content);
	grid-auto-flow: column;
	gap: ${({ theme }) => theme.spacing(2)};
	justify-items: start;
	${({ theme }) => theme.breakpoints.tablet} {
		grid-template-rows: min-content;
		justify-items: inherit;
	}
`;

const HeaderEnd = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(2)};
	justify-content: end;
`;

const StyledCardBody = styled(CardBody)<{ $isFilterActive: boolean }>`
	padding: 0;
	margin-top: ${({ $isFilterActive, theme }) => ($isFilterActive ? 0 : theme.spacing(4))};
	display: grid;
	grid-template-columns: 1fr;
	gap: ${({ theme }) => theme.spacing(6)};
	align-content: stretch;
	${({ theme }) => theme.breakpoints.tablet} {
		grid-template-columns: 1fr 1fr;
	}
	${({ theme }) => theme.breakpoints.desktop} {
		grid-template-columns: 1fr 1fr 1fr;
	}
`;

const StyledSwitchControl = styled(SwitchControl)`
	margin: 0;
`;

export const EventOverviewPage: FC<EventScopeProps> = ({ showOnlyOwnEvents, onToggle }) => {
	const { activeFilterId } = useFilter();

	return (
		<PaginatedListProvider
			listFetchConfig={laravelPaginatedListFetchConfig(
				[...eventService.listOverviewKey, `${activeFilterId}`, `showOnlyOwnEvents=${showOnlyOwnEvents ? "1" : "0"}`],
				(config: PaginatedListFetcherConfig<EventListOverviewResource>) =>
					eventService.listOverview(config, activeFilterId, showOnlyOwnEvents),
			)}
		>
			<EventOverviewPageContent showOnlyOwnEvents={showOnlyOwnEvents} onToggle={onToggle} />
		</PaginatedListProvider>
	);
};

const EventOverviewPageContent: FC<EventScopeProps> = ({ showOnlyOwnEvents, onToggle }) => {
	const { t } = useTranslation();
	const { data: events, setPage, pagination, isEmpty, setSearchParams } = usePaginatedList<EventListOverviewResource>();
	const { activeFilterId, resetFilter, applyFilter } = useFilter();
	const filterDrawerProps = useEditDrawer();
	const { data: predefinedFilter } = useFilterQuery({ filterId: activeFilterId });
	const handleSave = useEventSave("overview");
	const openFilterDrawer = useCallback(() => {
		filterDrawerProps.handleOpen(activeFilterId || "new");
	}, [activeFilterId, filterDrawerProps]);

	return (
		<div>
			<Wrapper>
				<Header>
					<HeaderTop>
						<CardHeading>
							<EventListCalendarSwitcher />
						</CardHeading>
						<HeaderEnd>
							<StyledSwitchControl
								name="showOnlyOwnEvents"
								label={t("Only my events")}
								value={showOnlyOwnEvents}
								onChange={onToggle}
							/>
							<DataListSearch
								setSearchParams={setSearchParams}
								searchParams={{
									search: "",
									searchFields: [
										{ label: t("Name"), name: "event_translations.name" },
										{ label: t("Project managers"), name: "projectManagers" },
									],
								}}
							/>
							<CreateEventDrawer
								createPath="/events/overview/create"
								detailsPath="/events/overview"
								handleSubmit={handleSave}
							/>
							<IconButton title={t("Open event filter")} onClick={openFilterDrawer} size="small" icon="filter" />
						</HeaderEnd>
					</HeaderTop>
					<FilterBar filterId={activeFilterId} onRemove={resetFilter} onClick={openFilterDrawer} />
				</Header>
			</Wrapper>
			<StyledCardBody $isFilterActive={!!activeFilterId}>
				{isEmpty ? (
					<div>{t("No events available")}</div>
				) : (
					events.map((x) => <EventOverviewCard key={x.id} event={x} />)
				)}
			</StyledCardBody>
			<CardFooter>
				<Pagination pagination={pagination} onChange={setPage} />
			</CardFooter>
			{filterDrawerProps.editEntityId && (
				<EventFilterDrawer {...filterDrawerProps} filter={predefinedFilter || null} onSubmit={applyFilter} />
			)}
		</div>
	);
};
